import React, { useCallback } from 'react';
import { Plus, Upload, Film } from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { AnimationListItem } from './AnimationListItem';
import { cn } from '../../utils/cn';

interface AnimationListProps {
  className?: string;
}

export const AnimationList: React.FC<AnimationListProps> = ({ className }) => {
  const {
    animations,
    selectedAnimationId,
    createAnimation,
    selectAnimation,
  } = useAnimationStore();

  const animationList = Object.values(animations);

  const handleCreate = useCallback(() => {
    createAnimation();
  }, [createAnimation]);

  const handleImport = useCallback(() => {
    // TODO: Implement animation import functionality
    console.log('Import animation clicked');
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      selectAnimation(id);
    },
    [selectAnimation]
  );

  return (
    <div className={cn('flex flex-col h-full bg-zinc-900', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-sm font-medium text-zinc-300">Animations</span>
        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded hover:bg-zinc-700 transition-colors text-zinc-400 hover:text-zinc-200"
            onClick={handleImport}
            title="Import Animation"
          >
            <Upload size={14} />
          </button>
          <button
            className="p-1.5 rounded hover:bg-zinc-700 transition-colors text-zinc-400 hover:text-zinc-200"
            onClick={handleCreate}
            title="Create New Animation"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Animation list */}
      <div className="flex-1 overflow-y-auto p-2">
        {animationList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-500 py-8">
            <Film size={32} className="mb-2 opacity-50" />
            <span className="text-sm">No animations</span>
            <button
              className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              onClick={handleCreate}
            >
              Create your first animation
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {animationList.map((animation) => (
              <AnimationListItem
                key={animation.id}
                animation={animation}
                isSelected={animation.id === selectedAnimationId}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer with count */}
      {animationList.length > 0 && (
        <div className="px-3 py-1.5 border-t border-zinc-700 text-xs text-zinc-500">
          {animationList.length} animation{animationList.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};
